/**
 * Performance Budget Configuration
 * Enforces size limits for bundles in CI/CD pipeline
 */

export interface BudgetEntry {
  name: string;
  maxSize: number; // in KB
  type: "js" | "css" | "total";
}

export const performanceBudgets: BudgetEntry[] = [
  // JavaScript budgets
  { name: "react-vendor", maxSize: 150, type: "js" },
  { name: "router", maxSize: 30, type: "js" },
  { name: "ui-radix", maxSize: 200, type: "js" },
  { name: "data", maxSize: 150, type: "js" },
  { name: "forms", maxSize: 80, type: "js" },
  { name: "charts", maxSize: 300, type: "js" },
  { name: "i18n", maxSize: 50, type: "js" },
  { name: "date", maxSize: 30, type: "js" },
  
  // Page budgets (initial JS for route)
  { name: "index-page", maxSize: 100, type: "js" },
  { name: "dashboard-page", maxSize: 150, type: "js" },
  { name: "skill-page", maxSize: 120, type: "js" },
  
  // CSS budgets
  { name: "main-css", maxSize: 100, type: "css" },
  
  // Total app budget
  { name: "total-initial", maxSize: 500, type: "total" }, // First load JS
  { name: "total-css", maxSize: 150, type: "total" },
];

// Lighthouse thresholds
export const lighthouseThresholds = {
  performance: 85,
  accessibility: 95,
  bestPractices: 90,
  seo: 95,
  pwa: 80,
};

// Core Web Vitals targets
export const coreWebVitals = {
  LCP: 2500, // Largest Contentful Paint (ms)
  FID: 100, // First Input Delay (ms)
  CLS: 0.1, // Cumulative Layout Shift
  FCP: 1800, // First Contentful Paint (ms)
  TTFB: 800, // Time to First Byte (ms)
  INP: 200, // Interaction to Next Paint (ms)
};

/**
 * Check if a bundle exceeds its budget
 */
export function checkBudget(
  bundleName: string,
  sizeKB: number,
  budgets: BudgetEntry[] = performanceBudgets
): { passed: boolean; budget?: BudgetEntry; overage?: number } {
  const budget = budgets.find((b) => bundleName.includes(b.name));
  
  if (!budget) {
    return { passed: true };
  }
  
  const passed = sizeKB <= budget.maxSize;
  return {
    passed,
    budget,
    overage: passed ? undefined : sizeKB - budget.maxSize,
  };
}

/**
 * Format budget report for CI output
 */
export function formatBudgetReport(
  results: Array<{ name: string; size: number; passed: boolean; budget?: number }>
): string {
  const lines = [
    "📊 Performance Budget Report",
    "═".repeat(50),
  ];
  
  for (const result of results) {
    const status = result.passed ? "✅" : "❌";
    const budgetStr = result.budget ? ` (budget: ${result.budget}KB)` : "";
    lines.push(`${status} ${result.name}: ${result.size.toFixed(1)}KB${budgetStr}`);
  }
  
  const failed = results.filter((r) => !r.passed);
  if (failed.length > 0) {
    lines.push("");
    lines.push(`⚠️ ${failed.length} bundle(s) exceeded budget`);
  } else {
    lines.push("");
    lines.push("✅ All bundles within budget");
  }
  
  return lines.join("\n");
}
